import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { DatePickerModal } from 'react-native-paper-dates';
import { TextInput } from './TextInput';

export type DateRange = {
  startDate: Date | undefined;
  endDate: Date | undefined;
};

type Props = {
  label: string;
  showLabel?: boolean;
  value?: DateRange;
  onChange: (range: DateRange) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
  testID?: string;
};

export function DateRangeInput({
  label,
  showLabel = true,
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  minimumDate,
  maximumDate,
  testID,
}: Props) {
  const [showPicker, setShowPicker] = useState(false);

  const formatDisplayValue = (): string => {
    if (!value) return '';
    const { startDate, endDate } = value;
    if (!startDate && !endDate) return '';
    if (startDate && !endDate) return startDate.toLocaleDateString();
    if (!startDate && endDate) return endDate.toLocaleDateString();
    return `${startDate!.toLocaleDateString()} - ${endDate!.toLocaleDateString()}`;
  };

  const handleConfirm = ({
    startDate,
    endDate,
  }: {
    startDate?: Date;
    endDate?: Date;
  }) => {
    onChange({ startDate, endDate });
    setShowPicker(false);
  };

  const handlePress = () => {
    if (!disabled) {
      setShowPicker(true);
    }
  };

  return (
    <View style={styles.container}>
      <Pressable onPress={handlePress} disabled={disabled}>
        <TextInput
          label={label}
          showLabel={showLabel}
          value={formatDisplayValue()}
          placeholder={placeholder ?? 'Select date range'}
          editable={false}
          pointerEvents="none"
          error={error}
          testID={testID}
          dense={true}
        />
      </Pressable>

      <DatePickerModal
        locale="en"
        mode="range"
        visible={showPicker}
        onDismiss={() => setShowPicker(false)}
        startDate={value?.startDate}
        endDate={value?.endDate}
        onConfirm={handleConfirm}
        validRange={{
          startDate: minimumDate,
          endDate: maximumDate,
        }}
        presentationStyle="pageSheet"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
});
